import React, { ReactElement } from 'react';
import { DateTimeField, useShowContextRequired } from 'ui-builder';
import { Permission } from 'nekst-api';
import { IconButton, IconType, PersonWidget, UserLogoSize, ShadowedCard, TextWithLinksField, usePopupFormManager } from 'features/nekst-widgets';
import { CommentType, CommentRequestType } from './EditCommentForm';
import ShadcnNoteForm from './ShadcnNoteForm';

interface Props<
  DataType extends CommentType = CommentType,
  RequestType extends CommentRequestType = CommentRequestType,
> {
  updatePermission: Permission;
  deletePermission: Permission;
  deleteItemFunc: (id: number) => Promise<void>;
  updateItemFunc: (id: number, data: RequestType) => Promise<DataType>;
  additionalInputs?: ReactElement;
  viewFields?: ListOrSingle<ReactElement>;
  dateTimeText?: string;
  textSource?: string;
}

export function ShadcnRowLayout(
  props: {
    icon?: ReactElement;
    mainBlockContent: ReactElement;
    dateSource: string;
    children?: ReactElement;
    dateTimeText?: string;
  },
) {
  return (
    <div className="flex items-start gap-3">
      {props.icon && <div className="shrink-0">{props.icon}</div>}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <div className="flex-1 min-w-0">{props.mainBlockContent}</div>
          <span className="text-app-xs text-gray-400 shrink-0">
            {props.dateTimeText || <DateTimeField source={props.dateSource} />}
          </span>
        </div>
        {props.children}
      </div>
    </div>
  );
}

export default function ShadcnCommentRow<
  Data extends CommentType = CommentType,
  Request extends CommentRequestType = CommentRequestType,
>(
  props: Props<Data, Request>,
) {
  const showContext = useShowContextRequired<Data>();

  const popupFormManager = usePopupFormManager();

  const comment = showContext.data!;
  const profile = comment._author;

  const openEditForm = () => {
    popupFormManager.openForm(
      <ShadcnNoteForm<Data, Request>
        title="Edit Note"
        submitFunc={async (data) => props.updateItemFunc(comment.id, data)}
        initialData={comment as unknown as Request}
        additionalInputs={props.additionalInputs}
      />,
      true,
    );
  };

  const hasPermission = (permission: Permission) => {
    return comment.permissions.includes(permission);
  };

  const onDelete = async () => {
    showContext.setLoading(true);
    await props.deleteItemFunc(comment.id);
    showContext.setLoading(false);
  };

  return (
    <ShadowedCard className="px-4 py-3">
      <div className="text-app-body text-gray-700 leading-relaxed whitespace-pre-wrap">
        {props.viewFields || (
          <TextWithLinksField source={props.textSource || 'text'} />
        )}
      </div>
      <footer className="flex items-center gap-2 min-w-0 mt-1.5">
        <PersonWidget profile={profile} size={UserLogoSize.SMALL} />
        <span className="text-app-xs text-gray-400 min-w-0 flex-1">
          {props.dateTimeText || <DateTimeField source="createdAt" className="text-app-xs text-gray-400" />}
        </span>
        {hasPermission(props.updatePermission) && (
          <IconButton
            title="Update"
            onClick={openEditForm}
            type={IconType.PENCIL}
          />
        )}
        {hasPermission(props.deletePermission) && (
          <IconButton
            title="Delete Comment"
            onClick={onDelete}
            type={IconType.TRASH_CAN}
          />
        )}
      </footer>
    </ShadowedCard>
  );
}
